import { useEffect, useRef, useState } from 'react'
import { Paperclip, X } from 'lucide-react'
import { z } from 'zod'

import { Button } from '@/ui/button'
import { Input } from '@/ui/input'
import { useAnnounce } from '@/shared/providers/announcer-provider'
import type { AssistantImage } from '../../api/assistant-api'

const logoFileSchema = z.file()
  .min(1, 'Choose a non-empty logo file.')
  .max(1024 * 1024, 'Logo files must be 1 MiB or smaller.')
  .mime(['image/png', 'image/jpeg', 'image/webp'], 'Choose a PNG, JPEG, or WebP logo.')
  .refine((file) => file.name.length <= 256, 'Choose a logo with a shorter file name.')

export function AssistantLogoAttachment({
  image,
  onChange,
  onPreparingChange,
  disabled,
}: {
  image: AssistantImage | null
  onChange: (image: AssistantImage | null) => void
  onPreparingChange: (preparing: boolean) => void
  disabled: boolean
}) {
  const attachRef = useRef<HTMLButtonElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const readerRef = useRef<FileReader | null>(null)
  const [error, setError] = useState('')
  const announce = useAnnounce()

  useEffect(() => () => {
    const reader = readerRef.current
    readerRef.current = null
    reader?.abort()
    onPreparingChange(false)
  }, [onPreparingChange])

  function selectFile(file: File | undefined) {
    const previous = readerRef.current
    readerRef.current = null
    previous?.abort()
    onPreparingChange(false)
    onChange(null)
    setError('')
    if (!file) return
    const parsed = logoFileSchema.safeParse(file)
    if (!parsed.success) {
      const message = parsed.error.issues[0]?.message ?? 'Choose a valid logo file.'
      setError(message)
      announce(message)
      return
    }
    const reader = new FileReader()
    readerRef.current = reader
    onPreparingChange(true)
    reader.onload = () => {
      if (readerRef.current !== reader) return
      readerRef.current = null
      onPreparingChange(false)
      const result = reader.result
      const separator = typeof result === 'string' ? result.indexOf(',') : -1
      if (typeof result !== 'string' || separator < 0) {
        readFailed()
        return
      }
      onChange({
        id: crypto.randomUUID(),
        name: file.name,
        contentType: file.type,
        content: result.slice(separator + 1),
      })
      announce('Logo attached. Send a message to prepare its upload.')
    }
    reader.onerror = () => {
      if (readerRef.current !== reader) return
      readerRef.current = null
      onPreparingChange(false)
      readFailed()
    }
    reader.readAsDataURL(file)
  }

  function readFailed() {
    const message = 'The logo could not be read. Choose it again.'
    setError(message)
    announce(message)
  }

  return <div className="min-w-0">
    <div className="flex min-w-0 items-center gap-1">
      <Input
        ref={inputRef}
        type="file"
        tabIndex={-1}
        accept="image/png,image/jpeg,image/webp"
        aria-label="Logo file"
        className="sr-only"
        disabled={disabled}
        onChange={(event) => {
          const file = event.target.files?.[0]
          event.target.value = ''
          selectFile(file)
        }}
      />
      <Button ref={attachRef} type="button" variant="ghost" size="icon" aria-label="Attach logo" title="Attach a PNG, JPEG, or WebP logo, up to 1 MiB" disabled={disabled} onClick={() => inputRef.current?.click()}>
        <Paperclip className="size-3.5" />
      </Button>
      {image && <>
        <span className="text-muted-foreground min-w-0 truncate text-[11px]" title={image.name}>{image.name}</span>
        <Button type="button" variant="ghost" size="icon-sm" aria-label={`Remove ${image.name}`} disabled={disabled} onClick={() => {
          onChange(null)
          attachRef.current?.focus()
          announce('Attached logo removed.')
        }}>
          <X className="size-3" />
        </Button>
      </>}
    </div>
    {error && <p className="text-destructive text-[11px]">{error}</p>}
  </div>
}
